'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { AdUnitContext } from '@/tracking/ads/components/AdUnitContext';
import { TopAd } from '@/tracking/ads/components/TopAd';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { HeaderNav, HeaderNavCurLink } from '@/components/common/HeaderNav';
import { BottomMountains } from '@/components/blocks/BottomMountains';
import { Footer } from '@/components/blocks/Footer';
import { Container } from '@/components/ui/container';
import { Heading } from '@/components/ui/heading';
import { Text } from '@/components/ui/text';
import { RouteAndAnalyticsProps } from '@/page-components/pathTypes';
import { fetchNearbyResorts } from '@/api/home/static';
import { IBackgroundImages } from '@/api/home/types';
import { useQueryWithInitialValue } from '@/util/useQueryWithInitialValue';
import IntegrityError from '@/page-components/400';
import { usePosition } from '@/util/usePosition';
import { IRegionFeaturedResort } from '@/api/region/types';
import { SpinnerCenteredWrapper } from '@/components/Spinner/SpinnerCenteredWrapper';
import { NearbyResortsBlock } from '@/components/home/NearbyResorts';
import { NearbyLocationInput } from '@/components/home/NearbyResorts/components/NearbyLocationInput';
import { useURILocation } from '@/util/useLocationFromURL';

// Global "Nearby Resorts" landing page. Adopts the same v3 chrome as
// /skireport and /ski-resorts: big H1 + supporting copy, no
// HeroNav/BoxWithShadow wrappers, flat space-y-10 column. The location
// picker and the results grid (NearbyLocationInput +
// NearbyResortsBlock) keep their own internal styling.

export type NearbyResortsProps = {
  backgroundImage: IBackgroundImages;
  featured: IRegionFeaturedResort;
  predefined: {
    lat: number,
    lng: number,
    place: string
  } | null
} & RouteAndAnalyticsProps;

export const TEMPLATE_ID = '1.3';
export const TEMPLATE_NAME = 'Nearby Resorts';

export default function NearbyResorts({
  backgroundImage,
  featured,
  predefined,
}: NearbyResortsProps) {
  const { t } = useOTSTranslation('common');
  const { coords, isLoading: isPositionLoading, error: positionError } = usePosition();

  const { place: URIplace, updateURI } = useURILocation(predefined);

  const [currentPos, setCurrentPos] = useState<{ latitude: number, longitude: number, title: string } | null>(URIplace ?? null);
  const isCustomPlace = !!currentPos?.title;

  if (!featured || !backgroundImage) {
    return <IntegrityError />;
  }

  const { current: curNearby, isLoading } = useQueryWithInitialValue(
    ['fetchNearbyResorts', currentPos?.latitude, currentPos?.longitude],
    async () => fetchNearbyResorts(currentPos?.latitude, currentPos?.longitude),
    [],
    { placeholderData: (prev) => prev },
  );

  useEffect(() => {
    if (!currentPos && coords) {
      setCurrentPos({ latitude: coords.latitude, longitude: coords.longitude, title: '' });
    }
  }, [coords, currentPos, isCustomPlace]);

  const isNearbyEmpty = !curNearby || curNearby.length === 0;
  const showGlobalLoading = (isPositionLoading || isLoading) && isNearbyEmpty;

  const handleLocationChange = useCallback((location?: { latitude: number, longitude: number, title: string }) => {
    setCurrentPos(location ?? null);

    if (location) {
      updateURI(`lat=${location.latitude}&lng=${location.longitude}&place=${encodeURIComponent(location.title)}`);
    } else {
      updateURI('');
    }
  }, []);

  const heroTitle = currentPos?.title
    ? t('nearbyResorts.heroTitleLoc', { location: currentPos?.title })
    : t('nearbyResorts.heroTitle');

  return (
    <AdUnitContext
      adType="home"
      adTKey="other travel"
      meta={{
        Fs: undefined,
        Rs: undefined,
        Th: undefined,
        Tl: undefined,
        snow: undefined,
        Wc: undefined,
      }}
    >
      <HeaderNav current={HeaderNavCurLink.TRIP_PLANNING} />
      <TopAd id="ski-resorts" />
      <Container width="xl-only" className="content-container">
        <div className="mt-4 space-y-10 px-4 pb-16 md:mt-8 md:px-0">
          <header className="space-y-4">
            <Heading
              level={1}
            >
              {heroTitle}
            </Heading>
            <Text variant="default" className="max-w-3xl text-base leading-relaxed text-foreground/85">
              {t('nearbyResorts.seoText')}
            </Text>
          </header>

          <NearbyLocationInput
            isCustom={isCustomPlace}
            positionError={!!positionError}
            setLocationCoords={handleLocationChange}
            initialLocationTitle={currentPos?.title}
          />

          {showGlobalLoading && <SpinnerCenteredWrapper />}
          {curNearby && <NearbyResortsBlock resorts={curNearby} from={currentPos} isFresh={!isLoading} />}
        </div>
      </Container>
      <BottomMountains />
      <Footer />
    </AdUnitContext>
  );
}

// fetchProps moved to ./<filename>.server.ts so the App Router RSC page can
// import the data fetcher without pulling in this 'use client' bundle.
